import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useEditLayout } from '../../contexts/EditLayoutContext';
import { useEditModeLookups } from '../../contexts/EditModeLookupsContext';
import { useApi } from '../../api/api';
import { COMPONENT_SLUGS, getComponent, slugToLabel } from '../../pages/registry/componentRegistry';
import type { ComponentSlug } from '../../pages/registry/componentRegistry';
import type { LayoutPanel } from '../../types/layout';
import { EDIT_MODE_SECTIONS } from '../../config/editModeSections';
import Modal from '../common/Modal/Modal';
import ErrorBoundary from '../../features/mapping/ErrorBoundary/ErrorBoundary';
import './LayoutEditorControls.css';

function getSectionAssignments(panel: LayoutPanel | null): Record<string, Array<{ slug: string; component_default_values: Record<string, unknown> | null }>> {
  const result: Record<string, Array<{ slug: string; component_default_values: Record<string, unknown> | null }>> = {};
  if (!panel?.sections?.length) return result;
  for (const section of panel.sections) {
    const comps = section.components
      .sort((a, b) => a.component_order - b.component_order)
      .map((c) => ({ slug: c.component_slug, component_default_values: c.component_default_values ?? null }));
    result[section.section_slug] = comps;
  }
  return result;
}

function getSectionOrder(panel: LayoutPanel | null): string[] {
  if (!panel?.sections?.length) return [];
  return panel.sections
    .sort((a, b) => a.section_order - b.section_order)
    .map((s) => s.section_slug);
}

function slugsFromPanel(panel: LayoutPanel | null): string[] {
  if (!panel?.sections?.length) return [];
  return panel.sections.flatMap((s) => s.components.map((c) => c.component_slug));
}

const DEFAULT_VALUES_PLACEHOLDER = JSON.stringify({ default: '' }, null, 2);

interface DefaultValuesModalProps {
  slug: ComponentSlug;
  sectionSlug: string;
  initialValues: Record<string, unknown> | null;
  onSave: (values: Record<string, unknown> | null) => void;
  onClose: () => void;
}

function DefaultValuesModal({ slug, sectionSlug, initialValues, onSave, onClose }: DefaultValuesModalProps) {
  const [text, setText] = useState(() =>
    initialValues && Object.keys(initialValues).length > 0
      ? JSON.stringify(initialValues, null, 2)
      : DEFAULT_VALUES_PLACEHOLDER
  );
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    try {
      const parsed = JSON.parse(text || '{}');
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setError('Must be a JSON object');
        return;
      }
      onSave(Object.keys(parsed).length > 0 ? parsed : null);
      onClose();
    } catch {
      setError('Invalid JSON');
    }
  };

  return (
    <Modal title={`Default Values: ${slugToLabel(slug)} (${slug})`} onClose={onClose} size="medium">
      <div className="layout-editor-default-values-modal">
        <p className="layout-editor-default-values-hint">
          Edit JSON for component_default_values. Empty object will be saved as null.
        </p>
        <textarea
          className="layout-editor-default-values-textarea"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setError(null);
          }}
          rows={12}
          spellCheck={false}
        />
        {error && <div className="layout-editor-default-values-error">{error}</div>}
        <div className="layout-editor-default-values-actions">
          <button type="button" className="layout-editor-preview-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="layout-editor-save-btn" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface ComponentPreviewModalProps {
  slug: ComponentSlug;
  onClose: () => void;
}

function ComponentPreviewModal({ slug, onClose }: ComponentPreviewModalProps) {
  const Comp = getComponent(slug);
  const props = {
    componentSlug: slug,
    componentName: slugToLabel(slug),
    defaultValues: null,
    injections: undefined,
  };

  return (
    <Modal title={`Preview: ${slugToLabel(slug)} (${slug})`} onClose={onClose} size="large" bodyStyle={{ maxHeight: '60vh', overflow: 'auto' }}>
      <div className="component-preview-content">
        <ErrorBoundary>
          <Comp {...props} />
        </ErrorBoundary>
      </div>
    </Modal>
  );
}

interface SectionEditorProps {
  panelType: 'left' | 'right';
  panel: LayoutPanel | null;
}

function SectionEditor({ panelType, panel }: SectionEditorProps) {
  const ctx = useEditLayout();
  const lookups = useEditModeLookups();
  const [previewSlug, setPreviewSlug] = useState<ComponentSlug | null>(null);
  const [defaultValuesTarget, setDefaultValuesTarget] = useState<{ sectionSlug: string; componentSlug: ComponentSlug } | null>(null);

  const componentSlugs = useMemo(
    () => (lookups?.panelComponents?.length ? lookups.panelComponents.map((c) => c.slug) : [...COMPONENT_SLUGS]),
    [lookups?.panelComponents]
  );
  const sectionMeta = useMemo(() => {
    if (lookups?.panelSections?.length) {
      return Object.fromEntries(
        lookups.panelSections.map((s) => [s.slug, { section_slug: s.slug, section_title: s.title || s.slug }])
      );
    }
    return Object.fromEntries(EDIT_MODE_SECTIONS.map((s) => [s.section_slug, s]));
  }, [lookups?.panelSections]);

  if (!ctx) return null;

  const {
    addComponentToSection,
    removeComponentFromSection,
    addSectionToPanel,
    removeSectionFromPanel,
    moveSection,
    moveComponentInSection,
    updateComponentDefaultValues,
  } = ctx;

  const assignments = getSectionAssignments(panel);
  const sectionOrder = getSectionOrder(panel);
  const usedSlugs = slugsFromPanel(panel);
  const availableSlugs = componentSlugs.filter((s) => !usedSlugs.includes(s));

  const sectionsForAddDropdown = (lookups?.panelSections ?? [])
    .filter((s) => s.slug !== 'active-data-layers' && !sectionOrder.includes(s.slug))
    .sort((a, b) => a.id - b.id);

  return (
    <>
      <div className="layout-editor-sections">
        {sectionOrder.length === 0 && (
          <div className="layout-editor-section-empty">
            {sectionsForAddDropdown.length > 0 ? 'No sections yet. Add one below.' : 'No sections. Add one when lookups load.'}
          </div>
        )}
        {sectionOrder.map((sectionSlug) => {
          const meta = sectionMeta[sectionSlug];
          const components = (assignments[sectionSlug] ?? []).map((c) => c.slug);
          const componentData = assignments[sectionSlug] ?? [];
          const sectionIdx = sectionOrder.indexOf(sectionSlug);

          return (
            <div key={sectionSlug} className="layout-editor-section">
              <div className="layout-editor-section-header">
                <span className="layout-editor-section-title">{meta?.section_title ?? sectionSlug}</span>
                <div className="layout-editor-section-actions">
                  <button
                    type="button"
                    className="layout-editor-icon-btn"
                    onClick={() => moveSection(panelType, sectionSlug, 'up')}
                    disabled={sectionIdx === 0}
                    title="Move section up"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    className="layout-editor-icon-btn"
                    onClick={() => moveSection(panelType, sectionSlug, 'down')}
                    disabled={sectionIdx === sectionOrder.length - 1}
                    title="Move section down"
                  >
                    ▼
                  </button>
                  <button
                    type="button"
                    className="layout-editor-remove-btn"
                    onClick={() => removeSectionFromPanel(panelType, sectionSlug)}
                    title="Remove section"
                  >
                    ✕
                  </button>
                  <select
                    className="layout-editor-add-select"
                    value=""
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) {
                        addComponentToSection(panelType, sectionSlug, val as ComponentSlug);
                        e.target.value = '';
                      }
                    }}
                  >
                    <option value="">+ Add component</option>
                    {availableSlugs.map((slug) => {
                      const comp = lookups?.panelComponents?.find((c) => c.slug === slug);
                      const label = comp?.title ?? slugToLabel(slug);
                      return (
                        <option key={slug} value={slug}>
                          {label} ({slug})
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div className="layout-editor-section-components">
                {components.length === 0 ? (
                  <div className="layout-editor-section-empty">No components</div>
                ) : (
                  componentData.map((comp, compIdx) => {
                    const slug = comp.slug as ComponentSlug;
                    return (
                      <div key={slug} className="layout-editor-component-row">
                        <span className="layout-editor-component-label">
                          {slugToLabel(slug)} ({slug})
                        </span>
                        <div className="layout-editor-component-actions">
                          <button
                            type="button"
                            className="layout-editor-icon-btn"
                            onClick={() => moveComponentInSection(panelType, sectionSlug, slug, 'up')}
                            disabled={compIdx === 0}
                            title="Move up"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="layout-editor-icon-btn"
                            onClick={() => moveComponentInSection(panelType, sectionSlug, slug, 'down')}
                            disabled={compIdx === componentData.length - 1}
                            title="Move down"
                          >
                            ▼
                          </button>
                          <button
                            type="button"
                            className="layout-editor-preview-btn"
                            onClick={() => setPreviewSlug(slug)}
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            className="layout-editor-preview-btn"
                            onClick={() => setDefaultValuesTarget({ sectionSlug, componentSlug: slug })}
                          >
                            Default Values
                          </button>
                          <button
                            type="button"
                            className="layout-editor-remove-btn"
                            onClick={() => removeComponentFromSection(panelType, sectionSlug, slug)}
                            title="Remove"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
        {sectionsForAddDropdown.length > 0 && (
          <div className="layout-editor-add-section">
            <select
              className="layout-editor-add-select"
              value=""
              onChange={(e) => {
                const val = e.target.value;
                if (val) {
                  const sec = lookups?.panelSections?.find((s) => s.slug === val);
                  if (sec) addSectionToPanel(panelType, sec.slug, sec.title || sec.slug);
                  e.target.value = '';
                }
              }}
            >
              <option value="">+ Add section</option>
              {sectionsForAddDropdown.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.title || s.slug} ({s.slug})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {previewSlug &&
        createPortal(
          <ComponentPreviewModal slug={previewSlug} onClose={() => setPreviewSlug(null)} />,
          document.body
        )}
      {defaultValuesTarget &&
        createPortal(
          <DefaultValuesModal
            slug={defaultValuesTarget.componentSlug}
            sectionSlug={defaultValuesTarget.sectionSlug}
            initialValues={
              (assignments[defaultValuesTarget.sectionSlug] ?? []).find((c) => c.slug === defaultValuesTarget.componentSlug)
                ?.component_default_values ?? null
            }
            onSave={(values) => {
              updateComponentDefaultValues(panelType, defaultValuesTarget.sectionSlug, defaultValuesTarget.componentSlug, values);
            }}
            onClose={() => setDefaultValuesTarget(null)}
          />,
          document.body
        )}
    </>
  );
}

function getBottomPanelSlugs(panel: LayoutPanel | null): string[] {
  if (!panel?.sections?.length) return [];
  const section = panel.sections.find((s) => s.section_slug === 'active-data-layers');
  if (!section) return [];
  return section.components
    .sort((a, b) => a.component_order - b.component_order)
    .map((c) => c.component_slug);
}

interface BottomPanelSectionEditorProps {
  panel: LayoutPanel | null;
}

const BOTTOM_PANEL_SECTION_SLUG = 'active-data-layers';

function BottomPanelSectionEditor({ panel }: BottomPanelSectionEditorProps) {
  const ctx = useEditLayout();
  const lookups = useEditModeLookups();
  const [previewSlug, setPreviewSlug] = useState<ComponentSlug | null>(null);
  const [defaultValuesTarget, setDefaultValuesTarget] = useState<{ sectionSlug: string; componentSlug: ComponentSlug } | null>(null);
  const componentSlugs = useMemo(
    () => (lookups?.panelComponents?.length ? lookups.panelComponents.map((c) => c.slug) : [...COMPONENT_SLUGS]),
    [lookups?.panelComponents]
  );

  if (!ctx) return null;

  const { addBottomPanelLayer, removeBottomPanelLayer, moveBottomPanelLayer, updateComponentDefaultValues } = ctx;

  const assignments = getSectionAssignments(panel);
  const componentData = assignments[BOTTOM_PANEL_SECTION_SLUG] ?? [];
  const components = componentData.map((c) => c.slug);
  const availableSlugs = componentSlugs.filter((s) => !components.includes(s));

  return (
    <>
      <div className="layout-editor-sections">
      <div className="layout-editor-section">
        <div className="layout-editor-section-header">
          <span className="layout-editor-section-title">Active Data Layers</span>
          <select
            className="layout-editor-add-select"
            value=""
            onChange={(e) => {
              const val = e.target.value;
              if (val) {
                addBottomPanelLayer(val as ComponentSlug);
                e.target.value = '';
              }
            }}
          >
            <option value="">+ Add layer</option>
            {availableSlugs.map((slug) => {
              const comp = lookups?.panelComponents?.find((c) => c.slug === slug);
              const label = comp?.title ?? slugToLabel(slug);
              return (
                <option key={slug} value={slug}>
                  {label} ({slug})
                </option>
              );
            })}
          </select>
        </div>
        <div className="layout-editor-section-components">
          {componentData.length === 0 ? (
            <div className="layout-editor-section-empty">No layers</div>
          ) : (
            componentData.map((comp, compIdx) => {
              const slug = comp.slug as ComponentSlug;
              return (
                <div key={slug} className="layout-editor-component-row">
                  <span className="layout-editor-component-label">
                    {slugToLabel(slug)} ({slug})
                  </span>
                  <div className="layout-editor-component-actions">
                    <button
                      type="button"
                      className="layout-editor-icon-btn"
                      onClick={() => moveBottomPanelLayer(slug, 'up')}
                      disabled={compIdx === 0}
                      title="Move up"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="layout-editor-icon-btn"
                      onClick={() => moveBottomPanelLayer(slug, 'down')}
                      disabled={compIdx === componentData.length - 1}
                      title="Move down"
                    >
                      ▼
                    </button>
                    <button
                      type="button"
                      className="layout-editor-preview-btn"
                      onClick={() => setPreviewSlug(slug)}
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      className="layout-editor-preview-btn"
                      onClick={() => setDefaultValuesTarget({ sectionSlug: BOTTOM_PANEL_SECTION_SLUG, componentSlug: slug })}
                    >
                      Default Values
                    </button>
                    <button
                      type="button"
                      className="layout-editor-remove-btn"
                      onClick={() => removeBottomPanelLayer(slug)}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      </div>
      {previewSlug &&
        createPortal(
          <ComponentPreviewModal slug={previewSlug} onClose={() => setPreviewSlug(null)} />,
          document.body
        )}
      {defaultValuesTarget &&
        createPortal(
          <DefaultValuesModal
            slug={defaultValuesTarget.componentSlug}
            sectionSlug={defaultValuesTarget.sectionSlug}
            initialValues={
              componentData.find((c) => c.slug === defaultValuesTarget.componentSlug)?.component_default_values ?? null
            }
            onSave={(values) => {
              updateComponentDefaultValues('bottom', defaultValuesTarget.sectionSlug, defaultValuesTarget.componentSlug, values);
            }}
            onClose={() => setDefaultValuesTarget(null)}
          />,
          document.body
        )}
    </>
  );
}

function panelToApiBody(panel: LayoutPanel | null): { sections: Array<{ section_slug: string; section_order: number; components: Array<{ component_slug: string; component_order: number; component_default_values?: Record<string, unknown> | null }> }> } {
  if (!panel?.sections?.length) return { sections: [] };
  const sections = panel.sections
    .sort((a, b) => a.section_order - b.section_order)
    .map((s) => ({
      section_slug: s.section_slug,
      section_order: s.section_order,
      components: (s.components || [])
        .sort((a, b) => a.component_order - b.component_order)
        .map((c, i) => {
          const item: { component_slug: string; component_order: number; component_default_values?: Record<string, unknown> | null } = {
            component_slug: c.component_slug,
            component_order: i,
          };
          if (c.component_default_values != null && Object.keys(c.component_default_values).length > 0) {
            item.component_default_values = c.component_default_values;
          }
          return item;
        }),
    }));
  return { sections };
}

export interface LayoutEditorControlsProps {
  panelType: 'left' | 'right' | 'bottom';
  pageType?: string | null;
  resourceId?: number | null;
}

export default function LayoutEditorControls({ panelType, pageType, resourceId }: LayoutEditorControlsProps): React.ReactElement | null {
  const ctx = useEditLayout();
  const api = useApi();
  const updatePanelLayout = api?.updatePanelLayout;
  const [saveStatus, setSaveStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  if (!ctx) return null;

  const { editableLayout } = ctx;

  const hasPageContext = pageType && (resourceId != null || pageType === 'home');

  const handleSave = async () => {
    if (!updatePanelLayout || !hasPageContext) {
      setSaveError('No page context. Save disabled.');
      return;
    }
    const panel =
      panelType === 'left' ? editableLayout.leftPanel : panelType === 'right' ? editableLayout.rightPanel : panelType === 'bottom' ? editableLayout.bottomPanel : null;
    if (!panel) return;
    const panelSlug = panel.panel_slug;
    setSaveStatus('loading');
    setSaveError(null);
    try {
      await updatePanelLayout(pageType, resourceId ?? undefined, panelSlug, panelToApiBody(panel));
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ?? (err as Error)?.message ?? 'Failed to save';
      setSaveError(String(msg));
      setSaveStatus('error');
    }
  };

  if (panelType === 'left') {
    return (
      <div className="layout-editor-controls">
        <h3 className="layout-editor-title">Edit Left Panel</h3>
        <p className="layout-editor-subtitle">Assign components to sections and reorder</p>
        <SectionEditor panelType="left" panel={editableLayout.leftPanel} />
        {saveError && <div className="layout-editor-default-values-error">{saveError}</div>}
        <button type="button" className="layout-editor-save-btn" onClick={handleSave} disabled={saveStatus === 'loading' || !hasPageContext}>
          {saveStatus === 'loading' ? 'Saving…' : saveStatus === 'success' ? 'Saved' : 'Save'}
        </button>
      </div>
    );
  }

  if (panelType === 'right') {
    return (
      <div className="layout-editor-controls">
        <h3 className="layout-editor-title">Edit Right Panel</h3>
        <p className="layout-editor-subtitle">Assign components to sections and reorder</p>
        <SectionEditor panelType="right" panel={editableLayout.rightPanel} />
        {saveError && <div className="layout-editor-default-values-error">{saveError}</div>}
        <button type="button" className="layout-editor-save-btn" onClick={handleSave} disabled={saveStatus === 'loading' || !hasPageContext}>
          {saveStatus === 'loading' ? 'Saving…' : saveStatus === 'success' ? 'Saved' : 'Save'}
        </button>
      </div>
    );
  }

  if (panelType === 'bottom') {
    return (
      <div className="layout-editor-controls">
        <h3 className="layout-editor-title">Edit Data Layers</h3>
        <p className="layout-editor-subtitle">Assign layers to show at initialization and reorder</p>
        <BottomPanelSectionEditor panel={editableLayout.bottomPanel} />
        {saveError && <div className="layout-editor-default-values-error">{saveError}</div>}
        <button type="button" className="layout-editor-save-btn" onClick={handleSave} disabled={saveStatus === 'loading' || !hasPageContext}>
          {saveStatus === 'loading' ? 'Saving…' : saveStatus === 'success' ? 'Saved' : 'Save'}
        </button>
      </div>
    );
  }

  return null;
}
